import dayjs from 'dayjs';
import { createJSONStorage } from 'zustand/middleware';
import { createShallowStore, createStoreWithPersistAndSubscribe } from '@frontend/store';
import { Feature } from '../types';

interface SubscriptionTransitionState {
  isActive: boolean;
  quota?: number;
  expiryDate?: string;
}

interface SubscriptionTransitionStore {
  transitionStateMap: Record<string, Partial<Record<Feature, SubscriptionTransitionState>>>;

  // Actions
  setFeatureTransitionState: (data: {
    feature: Feature;
    state: SubscriptionTransitionState;
    locationId: string;
  }) => void;

  getFeatureTransitionState: (data: {
    feature: Feature;
    locationId: string;
  }) => SubscriptionTransitionState | undefined;

  reset: () => void;
}

const DEFAULT_EXPIRY_MINUTES = 15;

const getExpiryDate = (): string => {
  return dayjs().add(DEFAULT_EXPIRY_MINUTES, 'minute').toISOString();
};

const isExpired = (date?: string): boolean => {
  if (!date) return true;
  return dayjs().isAfter(dayjs(date));
};

export const useSubscriptionTransitionStore = createStoreWithPersistAndSubscribe<SubscriptionTransitionStore>(
  (set, get) => ({
    transitionStateMap: {},

    setFeatureTransitionState: ({
      feature,
      state,
      locationId,
    }: {
      feature: Feature;
      state: SubscriptionTransitionState;
      locationId: string;
    }) => {
      const locationState = get().transitionStateMap[locationId] || {};
      const currentFeatureState = locationState[feature] || {};

      const updatedFeatureState = {
        ...currentFeatureState,
        ...state,
        expiryDate: state.expiryDate || getExpiryDate(),
      };
      set((state) => ({
        transitionStateMap: {
          ...state.transitionStateMap,
          [locationId]: {
            ...locationState,
            [feature]: updatedFeatureState,
          },
        },
      }));
    },

    getFeatureTransitionState: ({ feature, locationId }: { feature: Feature; locationId: string }) => {
      const locationState = get().transitionStateMap[locationId] || {};
      const featureState = locationState[feature];

      if (featureState && isExpired(featureState.expiryDate)) {
        set((state) => ({
          transitionStateMap: {
            ...state.transitionStateMap,
            [locationId]: {
              ...locationState,
              [feature]: undefined,
            },
          },
        }));

        return undefined;
      }
      return featureState;
    },

    reset: () => {
      set({ transitionStateMap: {} });
    },
  }),
  {
    name: 'subscription-transition-store',
    storage: createJSONStorage(() => localStorage),
  },
  { name: 'subscription-transition-store', trace: true }
);

export const useSubscriptionTransitionShallowStore =
  createShallowStore<SubscriptionTransitionStore>(useSubscriptionTransitionStore);
